import React, { useCallback, useEffect } from 'react';
import { OmitStrict } from '@tanium/coreui-utils';
import { css, cx } from '@tanium/react-emotion';
import { TextInput } from '@tanium/react-text-input';
import { usePreviousValue } from '@tanium/react-use-previous-value';
import {
  AdvancedFilterInput,
  AdvancedFilterInputProps,
  RenderPropArgs,
} from './AdvancedFilterInput';

const TextInputStyle = css({
  width: 200,
});

export interface AdvancedFilterTextInputProps
  extends Pick<AdvancedFilterInputProps, 'label' | 'dataKey'>,
    React.ComponentPropsWithoutRef<typeof TextInput> {}

interface InternalAdvancedFilterTextInputProps
  extends OmitStrict<AdvancedFilterTextInputProps, 'dataKey'>,
    RenderPropArgs {}

/**
 * A simple text input for use in AdvancedFilters component.
 */
const InternalAdvancedFilterTextInput = ({
  updateFilter,
  idForInput,
  filterValue = '',
  className,
  label,
  ...rest
}: InternalAdvancedFilterTextInputProps) => {
  const previousLabel = usePreviousValue(label);
  useEffect(() => {
    if (previousLabel !== label) {
      updateFilter({ value: filterValue || undefined });
    }
  }, [updateFilter, label, previousLabel, filterValue]);

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value: text } }) => {
      updateFilter({ value: text || undefined });
    },
    [updateFilter],
  );

  return (
    <TextInput
      {...rest}
      className={cx(TextInputStyle, className)}
      value={filterValue}
      id={idForInput}
      onChange={handleChange}
    />
  );
};

export const AdvancedFilterTextInput = ({
  label,
  dataKey,
  ...rest
}: AdvancedFilterTextInputProps) => (
  <AdvancedFilterInput {...{ label, dataKey }}>
    {({ ...props }) => <InternalAdvancedFilterTextInput label={label} {...rest} {...props} />}
  </AdvancedFilterInput>
);
